import { type VariantActions } from '../lib/types/Loader';
import {
  createWidgetVariantFacClient,
  createWidgetBaseClient,
  createWidgetVariantEmbeddedClient,
  createWidgetVariantFabClient,
  createWidgetVariantFicClient,
} from '../lib/utils';
import { Variant } from './Variant';

type GetWidgetClientProps<V extends Variant> = {
  namespace?: string;
  variant?: V;
};

const defaultGetWidgetClientProps = {
  namespace: '_si_widget',
} as const;

/**
 * Gets the widget client for the specified variant without loading the widget script.
 * Note: This assumes the widget has already been loaded and initialized on the page.
 *
 * @template V - The variant type of the widget
 * @param {GetWidgetClientProps<V>} [props] - The properties to initialize the widget with.
 * @param {string} [props.namespace='_si_widget'] - The namespace for the loader script that will be created on the window object.
 * @param {V} props.variant - The variant of the widget.
 * @returns {VariantActions[V]} The actions interface specific to the provided widget variant.
 */
export const getWidgetClient = <V extends Variant>(
  {
    namespace = '_si_widget',
    variant,
  }: GetWidgetClientProps<V> = defaultGetWidgetClientProps as GetWidgetClientProps<V>
): VariantActions[V] | undefined => {
  if (typeof window !== 'undefined' && (window as unknown as { [key: string]: unknown })[namespace]) {
    switch (variant) {
      case Variant.FLOATING_ACTION_CARD:
        return createWidgetVariantFacClient(namespace) as VariantActions[V];
      case Variant.EMBEDDED:
        return createWidgetVariantEmbeddedClient(namespace) as VariantActions[V];
      case Variant.FLOATING_ACTION_BUTTON:
        return createWidgetVariantFabClient(namespace) as VariantActions[V];
      case Variant.FLOATING_INPUT_CARD:
        return createWidgetVariantFicClient(namespace) as VariantActions[V];
      default:
        return createWidgetBaseClient(namespace) as VariantActions[V];
    }
  }

  return undefined;
};
